import {useTranslation} from "react-i18next";
import TabBox from "../../../../../components/layout/TabBox.tsx";
import StatusBadge, {type StatusVariant} from "../../../../../components/StatusBadge.tsx";
import dateFormatter from "../../../../../utils/DateFormatter.ts";
import MailScheduleHint from "./MailScheduleHint.tsx";
import {type CustomerCommunication} from "../../../../../model/crm/CustomerCommunication.ts";

interface CustomerMailHistoryProps {
    communications: CustomerCommunication[];
    loading: boolean;
    sendCron?: string;
}

// Send-log states map onto the shared status colors; an unknown / missing state
// (communication never logged) stays neutral.
const stateVariant = (state?: string): StatusVariant => {
    switch (state) {
        case "SEND_SUCCESS": return "success";
        case "SEND_FAILED": return "error";
        case "PREPARED": return "info";
        default: return "neutral";
    }
};

// A customer's mail history as a table (#55218 Stufe 1). Since #55243 note #21
// only the read-only deleted-customer view uses it — the active detail page shows
// sent/planned mails inside the campaigns section instead.
const CustomerMailHistory = (props: CustomerMailHistoryProps) => {
    const {t} = useTranslation();

    // "Betreff" = i18n-resolved step type + brand/vehicle, aggregated into one
    // column (carlineNo intentionally dropped).
    const subjectOf = (communication: CustomerCommunication) => {
        const type = communication.stepType
            ? t(`crm.opportunities.types.${communication.stepType}`, communication.stepType)
            : "";
        const vehicle = [communication.brandName, communication.vehicleName].filter(Boolean).join(" ");
        return [type, vehicle].filter(Boolean).join(" · ");
    };

    const renderBody = () => {
        if (props.loading) {
            return <div className="text-sm text-an-muted-2 py-2">{t("crm.mailHistory.loading")}</div>;
        }
        if (props.communications.length === 0) {
            return <div className="text-sm text-an-muted-2 py-2">{t("crm.mailHistory.empty")}</div>;
        }
        return (
            <div className="bg-an-surface border border-an-border rounded-an-md shadow-an-xs overflow-x-auto font-lato">
                <table className="w-full max-w-full border-collapse">
                    <thead className="bg-an-panel">
                    <tr className="text-an-muted-2 text-xs tracking-wide">
                        <th className="text-left font-bold px-4 py-3">{t("crm.mailHistory.subject")}</th>
                        <th className="text-left font-bold px-4 py-3 whitespace-nowrap">{t("crm.mailHistory.sentAt")}</th>
                        <th className="text-left font-bold px-4 py-3">{t("crm.mailHistory.state")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {props.communications.map((communication, index) => (
                        <tr key={index} className="border-t border-an-border text-an-ink text-sm">
                            <td className="px-4 py-3 wrap-break-word">{subjectOf(communication) || "—"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                                {communication.sentAt ? dateFormatter.formatDateTime(communication.sentAt) : "—"}
                            </td>
                            <td className="px-4 py-3">
                                {communication.state
                                    ? <StatusBadge
                                        variant={stateVariant(communication.state)}
                                        label={t(`crm.mailHistory.states.${communication.state}`, communication.state)}/>
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <TabBox title={t("crm.mailHistory.title")}>
            {renderBody()}
            <MailScheduleHint sendCron={props.sendCron}/>
        </TabBox>
    );
};

export default CustomerMailHistory;
